<template>
	<AppPage>
		<AppCardPage
			class="meeting-content"
			@dragover.prevent="startFileDrop"
			@dragleave.prevent="endFileDrop"
			@drop.prevent="onFileDrop"
		>
			<AppCardPageTop>
				<h2 class="text-2xl">Gesprekken</h2>
				<div class="right">
					<input
						id="current-search"
						v-model="currentSearch"
						type="text"
						placeholder="Zoek een gesprek"
						@keydown.enter.prevent="submitSearch"
					/>
					<AppButton
						v-if="!recorder.recording"
						variant="danger"
						icon="fiber_manual_record"
						class="self-stretch my-0.5"
						:loading="startingRecording"
						@click="startRecording"
					>
						Gesprek opnemen
					</AppButton>
					<AppButton
						v-else-if="recorder.recording"
						variant="secondary"
						icon="stop"
						:loading="stoppingRecording"
						@click="stopRecording"
					>
						Stop opname
					</AppButton>
				</div>
			</AppCardPageTop>
			<div
				class="file-drop-outlined p-4 py-10 flex justify-center items-center border-dashed border-2 text-gray-400 rounded-sm mb-4"
				:class="{ 'border-primary': isDroppingFiles }"
			>
				<input
					:id="uploaderId"
					ref="filePicker"
					type="file"
					class="hidden"
					accept=".wav,.mp3,.aac,.ogg,.mpeg,.amr,.m4a,.mp4,.flac"
					@change="handleFileUpload"
				/>
				<span>
					Sleep hiernaartoe om een gesprek toe te voegen of
					<label
						class="link"
						:for="uploaderId"
						tabindex="0"
						@keydown.enter="openFilePicker"
					>
						kies een opname
					</label>
				</span>
			</div>

			<AppCard no-padding>
				<AppCardTable>
					<template #header>
						<AppCardTableHeader
							v-for="header in meetingHeaders"
							:key="header.index"
							:header="header"
							:sort-order="sortOrder"
							:sorted-column-model="sortedColumn"
							@sort="header.properties?.onSort"
						/>
					</template>
					<template #body>
						<tr
							v-if="filteredMeetings.length === 0 && status === 'complete'"
							class="meeting-row disabled nothing-found"
						>
							<td colspan="5">
								Geen gesprekken gevonden.
								<label
									class="link"
									:for="uploaderId"
									tabindex="0"
									@keydown.enter="openFilePicker"
									>Voeg een opname toe</label
								>
								<template v-if="!recorder.recording">
									of
									<AppButton
										variant="danger"
										icon="fiber_manual_record"
										class="inline-flex!"
										:loading="startingRecording"
										@click="startRecording"
									>
										begin met opnemen
									</AppButton>
								</template>
							</td>
						</tr>
						<MeetingsOverviewRow
							v-for="{ item: meeting } in filteredMeetings"
							v-else-if="meetings.length > 0"
							:key="meeting.id ?? undefined"
							:meeting="meeting"
						/>
					</template>
				</AppCardTable>
			</AppCard>
		</AppCardPage>
	</AppPage>
</template>

<script setup lang="ts">
import { ref, useTemplateRef, watch } from "vue";
import type { AppCardTableHeaderModel } from "@/models/AppCardTableHeaderModel";
import { useFuse } from "@vueuse/integrations/useFuse";
import { SortOrder } from "@/enums/SortOrder";
import { onKeyStroke } from "@vueuse/core";
import { elementEditable } from "@/helpers/inputHelpers";
import { getTitle } from "@/helpers/conversationHelpers";
import { useFiledropper } from "@/composables/useFiledropper";
import type { Conversation } from "@/generatedApi";
import { useQuery, useZero } from "@/composables/useZero";
import { useUserStore } from "@/store/useUserStore";
import { useRecorderStore } from "@/store/useRecorderStore";
import { useRouter } from "vue-router";
import { useGlobalUploadStore } from "@/store/useGlobalUploadStore";
import { omit } from "es-toolkit";
import type { Row } from "@rocicorp/zero";
import { mutators, queries, type Schema } from "@loanlink-nl/zero";
import { upload } from "@/helpers/networkHelpers";

const uploaderId = crypto.randomUUID();
const filePicker = useTemplateRef("filePicker");

const globalUploadStore = useGlobalUploadStore();

function openFilePicker() {
	filePicker.value?.click();
}

function handleFileUpload(event: Event) {
	const target = event.target as HTMLInputElement;
	if (target.files) {
		for (const file of Array.from(target.files)) {
			// We need to simulate a drag event for the useFiledropper composable
			const dataTransfer = new DataTransfer();
			dataTransfer.items.add(file);
			// Pass the DragEvent to onFileDrop by creating a new DragEvent
			onFileDrop(new DragEvent("drop", { dataTransfer }));
		}
	}
}

function handleFileDrop(file: File) {
	if (!file) return;

	if (!filePicker.value) return;
	filePicker.value.value = "";

	const meetingId = crypto.randomUUID();
	const recordingId = crypto.randomUUID();

	globalUploadStore.setUploadState(recordingId, {
		state: "uploading",
		progress: 0,
		filename: file.name,
	});

	globalUploadStore.setPendingUpload(recordingId, {
		file,
		documentId: recordingId,
		filename: file.name,
	});

	z.value.mutate(
		mutators.conversationTable.create({
			id: meetingId,
			agencyId: useUserStore().user?.agencyId ?? "",
			createdAt: Date.now(),
			type: "upload",
			filename: file.name,
			contentType: file.type,
			byteSize: file.size,
			recordingId: recordingId,
		}),
	);
}

async function performUpload(
	uploadUrl: string,
	file: File,
	documentId: string,
	meetingId: string,
) {
	try {
		const ok = await upload(uploadUrl, {
			method: "PUT",
			body: file,
			headers: {
				"x-ms-blob-type": "BlockBlob",
				"Content-Type": file.type,
			},
			onProgress: (progress) => {
				globalUploadStore.updateUploadProgress(documentId, progress);
			},
		});
		if (!ok) {
			throw new Error("Upload failed");
		}

		z.value.mutate(
			mutators.conversationTable.setStatus({
				id: meetingId,
				status: "ready_to_transcribe",
			}),
		);

		globalUploadStore.completeUpload(documentId);
	} catch (err) {
		globalUploadStore.setUploadError(
			documentId,
			err instanceof Error ? err.message : "Upload failed",
		);

		z.value.mutate(
			mutators.conversationTable.setStatus({
				id: meetingId,
				status: "errored",
			}),
		);

		throw err;
	}
}

const { isDroppingFiles, startFileDrop, endFileDrop, onFileDrop } =
	useFiledropper({
		handleFileDrop: (file: File) => {
			void handleFileDrop(file);
		},
	});

type AllowedSortColumns =
	| "createdAt"
	| "title"
	| "generatedTitle"
	| "displayId"
	| "durationInSeconds"
	| "status";

const currentSearch = ref("");
const sortedColumn = ref<AllowedSortColumns>("createdAt");
const sortOrder = ref<SortOrder>(SortOrder.Descending);

function sort(columnModel: AllowedSortColumns) {
	if (sortedColumn.value === columnModel) {
		sortOrder.value =
			sortOrder.value === SortOrder.Ascending
				? SortOrder.Descending
				: SortOrder.Ascending;
	} else {
		sortedColumn.value = columnModel;

		if (columnModel === "createdAt") {
			sortOrder.value = SortOrder.Descending;
		} else {
			sortOrder.value = SortOrder.Ascending;
		}
	}
}

const z = useZero();
const userStore = useUserStore();
const recorder = useRecorderStore();
const router = useRouter();

const startingRecording = ref(false);
const stoppingRecording = ref(false);

const { data: meetings, status } = useQuery(() =>
	queries.conversations.all({
		sortColumn: sortedColumn.value,
		sortOrder: sortOrder.value === SortOrder.Ascending ? "asc" : "desc",
	}),
);

// Watch for document tokens and trigger uploads when they become available
const { data: documentTokens } = useQuery(() =>
	queries.dossierDocumentTokens.withConversations(),
);

// Watch for new tokens and start uploads
watch(
	documentTokens,
	(tokens) => {
		if (!tokens) return;

		for (const token of tokens) {
			const pendingUpload = globalUploadStore.getPendingUpload(
				token.documentId,
			);
			const conversationId = token.dossierDocument?.conversation?.id;
			if (pendingUpload && token.uploadUrl && conversationId) {
				// Remove from pending uploads to avoid duplicate processing
				globalUploadStore.removePendingUpload(token.documentId);

				// Start the upload
				void performUpload(
					token.uploadUrl,
					pendingUpload.file,
					pendingUpload.documentId,
					conversationId,
				);
			}
		}
	},
	{ immediate: true, deep: true },
);

async function startRecording() {
	if (!userStore.user?.agencyId) {
		return;
	}

	startingRecording.value = true;
	try {
		const meetingId = crypto.randomUUID();

		// Create new conversation in Zero
		z.value.mutate(
			mutators.conversationTable.create({
				id: meetingId,
				agencyId: userStore.user.agencyId,
				createdAt: Date.now(),
				type: "recording",
			}),
		);

		void router.push({
			name: "Meeting",
			params: { meetingId },
		});

		await recorder.start(meetingId);
	} finally {
		startingRecording.value = false;
	}
}

async function stopRecording() {
	stoppingRecording.value = true;

	await recorder.stop().finally(() => {
		stoppingRecording.value = false;
	});
}

const meetingsMinimal = ref<
	Omit<Row<Schema["tables"]["conversationTable"]>, "summary">[]
>([]);

watch(
	meetings,
	() => {
		meetingsMinimal.value = meetings.value.map((meeting) =>
			omit(meeting, ["summary"]),
		);
	},
	{ deep: 2, immediate: true },
);

const { results: filteredMeetings } = useFuse(currentSearch, meetingsMinimal, {
	fuseOptions: {
		keys: ["title", "displayId"],
		getFn: (meeting, path) => {
			if (path[0] === "title") {
				return getTitle(meeting);
			}

			return String(meeting[path[0] as keyof typeof meeting]!);
		},
	},
	matchAllWhenSearchEmpty: true,
});

// Row focus keyboard navigation
function moveRow(e: Event, dir: "prev" | "next") {
	if (!(e.target instanceof HTMLElement)) {
		return;
	}

	if (elementEditable(e.target)) {
		return;
	}

	e.preventDefault();

	const currentRow = e.target.closest(".meeting-row");

	if (e.target.nodeName === "A" && currentRow) {
		const sibling = dir === "prev" ? "previousSibling" : "nextSibling";
		const nextRow = currentRow[sibling];

		if (!(nextRow instanceof HTMLElement)) {
			return;
		}

		if (nextRow.nodeValue !== "" && nextRow instanceof HTMLElement) {
			e.target.blur();
			nextRow.querySelector("a")?.focus();
		}

		return;
	}

	const nextMeetingRow = document.querySelector(
		`.meeting-row${dir === "prev" ? ":last-child" : ""} a`,
	);
	if (nextMeetingRow instanceof HTMLElement) {
		nextMeetingRow.focus();
	}
}

function submitSearch(e: Event) {
	if (e.target instanceof HTMLInputElement) {
		e.target.blur();
	}

	const firstMeetingRow = document.querySelector(".meeting-row a");
	if (firstMeetingRow instanceof HTMLElement) {
		firstMeetingRow.focus();
	}
}

// Keyboard shortcuts
onKeyStroke(["ArrowUp", "k"], (e) => {
	if (elementEditable(document.activeElement)) {
		return;
	}
	moveRow(e, "prev");
});

onKeyStroke(["ArrowDown", "j"], (e) => {
	if (elementEditable(document.activeElement)) {
		return;
	}
	moveRow(e, "next");
});

onKeyStroke("/", (e) => {
	if (elementEditable(document.activeElement)) {
		return;
	}

	e.preventDefault();
	document.getElementById("current-search")?.focus();
});

const meetingHeaders: AppCardTableHeaderModel<Conversation>[] = [
	{
		model: "title",
		label: "Onderwerp",
		index: 0,
		properties: {
			onSort: () => {
				sort("title");
			},
		},
	},
	{
		model: "createdAt",
		label: "Datum",
		width: 200,
		index: 1,
		properties: {
			onSort: () => {
				sort("createdAt");
			},
		},
	},
	{
		model: "durationInSeconds",
		label: "Lengte",
		width: 100,
		index: 2,
		properties: {
			onSort: () => {
				sort("durationInSeconds");
			},
		},
	},
];
</script>

<style lang="scss">
.meeting-content {
	#current-search {
		padding: 0.375rem 0.625rem;
		width: 250px;
		border-radius: var(--border-radius);
		border: 1px solid var(--border);
		background: white;
	}

	.app-card-table {
		th,
		td {
			border-left: none;
		}

		.table-cell-wrap {
			.column-name {
				display: flex;
				align-items: center;
				gap: 0.25rem;
			}
		}
	}

	.nothing-found {
		text-align: center;
		color: var(--text-muted);
		user-select: text;
	}
}

.app-card-page-top {
	.right {
		display: flex;
		align-items: center;
		column-gap: 10px;
	}
}

.meeting-row:not(.disabled):focus-within,
.app-card-table:not(:focus-within) .meeting-row:not(.disabled):hover {
	cursor: pointer;
	outline: none;

	td {
		background: var(--background-primary-hover);
	}

	td:first-child {
		border-radius: 0 0 0 calc(var(--border-radius) - 1px);
	}

	td:last-child {
		border-radius: 0 0 calc(var(--border-radius) - 1px) 0;
	}
}

.meeting-row {
	height: 12px;
	user-select: none;

	&.disabled td,
	td a {
		padding: 10px;
	}

	td {
		a {
			color: inherit;
			text-decoration: inherit;
			display: flex;
			align-items: center;
			height: 100%;
			white-space: nowrap;
			position: relative;
			gap: 0.5rem;

			&:focus {
				outline: none;
			}

			&:visited:hover {
				color: inherit;
			}
		}

		.recording-indicator {
			color: #dc2626;
			animation: pulse 2s infinite;

			.ht-icon {
				font-size: 16px;
			}
		}
	}
}

@keyframes pulse {
	0%,
	100% {
		opacity: 1;
	}
	50% {
		opacity: 0.5;
	}
}
</style>
